import { Fragment, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ProjectSelector } from '../components/ProjectSelector';
import { ChatPanel } from '../components/ChatPanel';
import { Button } from '../components/ui/Button';
import { PageHeader } from '../components/ui/PageHeader';
import { Spinner } from '../components/ui/Spinner';
import { UsageBadge } from '../components/UsageBadge';
import { useToast } from '../components/ui/Toast';
import { useApi } from '../hooks/useApi';
import { useProjects } from '../hooks/useProjects';
import { useRefetchOnFocus } from '../hooks/useRefetchOnFocus';
import { StatusBadge } from '../components/ui/StatusBadge';
import { EmptyState } from '../components/ui/EmptyState';
import { Markdown } from '../components/ui/Markdown';
import { SessionListPanel } from '../components/SessionListPanel';
import type { SessionRow, SessionSection } from '../components/SessionListPanel';
import type {
  Project,
  DaedalusSession,
  ReleaseTrack,
  PrometheusSession,
  PrometheusSessionStatus,
} from '@pantheon/shared';

const taskStatusConfig: Record<string, { text: string; color: string }> = {
  pending: { text: 'Pending', color: 'text-gray-500' },
  running: { text: 'Running...', color: 'text-blue-500' },
  completed: { text: 'Done', color: 'text-green-600' },
  failed: { text: 'Failed', color: 'text-red-500' },
};

const taskTypeIcons: Record<string, string> = {
  slack_message: 'Slack',
  confluence_update: 'Confluence',
  email_document: 'Email',
  gtm_asset: 'GTM Asset',
};

// status → display for the session list panel
const statusDisplay: Record<
  PrometheusSessionStatus,
  { text: string; badge: string; dot: string }
> = {
  pending: { text: 'Queued', badge: 'bg-blue-50 text-blue-600', dot: 'bg-blue-400 animate-pulse' },
  running: { text: 'Running', badge: 'bg-blue-50 text-blue-600', dot: 'bg-blue-400 animate-pulse' },
  waiting_approval: { text: 'Needs approval', badge: 'bg-yellow-50 text-yellow-700', dot: 'bg-yellow-400' },
  completed: { text: 'Completed', badge: 'bg-success-light text-success', dot: 'bg-green-400' },
  failed: { text: 'Failed', badge: 'bg-danger-light text-danger', dot: 'bg-red-400' },
};

const SESSION_GROUPS: { key: string; title: string; statuses: PrometheusSessionStatus[] }[] = [
  { key: 'in_progress', title: 'In Progress', statuses: ['pending', 'running'] },
  { key: 'needs_approval', title: 'Needs Approval', statuses: ['waiting_approval'] },
  { key: 'completed', title: 'Completed', statuses: ['completed'] },
  { key: 'failed', title: 'Failed', statuses: ['failed'] },
];

export function PrometheusPage() {
  const { get, post } = useApi();
  const { toast } = useToast();
  const { projects } = useProjects();
  const { sessionId: urlSessionId } = useParams();
  const navigate = useNavigate();
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [availableSessions, setAvailableSessions] = useState<DaedalusSession[]>([]);
  const [prometheusSessions, setPrometheusSessions] = useState<PrometheusSession[]>([]);
  const [tracks, setTracks] = useState<ReleaseTrack[]>([]);
  const [activeSession, setActiveSession] = useState<PrometheusSession | null>(null);
  const [selectedDaedalusId, setSelectedDaedalusId] = useState<string | null>(null);
  const [selectedTrackId, setSelectedTrackId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [approving, setApproving] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const getRef = useRef(get);
  getRef.current = get;

  const projectNameById = useMemo(() => {
    const m: Record<string, string> = {};
    for (const p of projects) m[p.projectId] = p.name;
    return m;
  }, [projects]);

  const trackNameById = useMemo(() => {
    const m: Record<string, string> = {};
    for (const t of tracks) m[t.trackId] = t.name;
    return m;
  }, [tracks]);

  const selectedProjectId = selectedProject?.projectId;

  // Session list — project selector is an optional filter. With a project we scope to it,
  // without one we show every project (the backend returns all when no query is passed).
  const fetchSessions = useCallback(
    async (showLoading = true) => {
      if (showLoading) setListLoading(true);
      const path = selectedProjectId
        ? `/prometheus/sessions?projectId=${selectedProjectId}`
        : '/prometheus/sessions';
      const result = await getRef.current<PrometheusSession[]>(path);
      if (result.success && result.data) setPrometheusSessions(result.data);
      if (showLoading) setListLoading(false);
    },
    [selectedProjectId],
  );

  // Project-scoped data needed by the Start New Release form (available sessions + tracks).
  const fetchProjectData = useCallback(async (projId: string) => {
    const [avail, tracksRes] = await Promise.all([
      getRef.current<DaedalusSession[]>(`/prometheus/available-sessions?projectId=${projId}`),
      getRef.current<ReleaseTrack[]>('/prometheus/tracks'),
    ]);
    if (avail.success && avail.data) setAvailableSessions(avail.data);
    if (tracksRes.success && tracksRes.data) setTracks(tracksRes.data);
  }, []);

  useEffect(() => {
    fetchSessions(true);
  }, [fetchSessions]);

  useEffect(() => {
    if (selectedProject) fetchProjectData(selectedProject.projectId);
    else setAvailableSessions([]);
  }, [selectedProject, fetchProjectData]);

  // Refresh lists + the open session when the user returns to the tab, so a run that
  // finished (or paused for approval) while they were away shows its real status.
  const activeSessionRef = useRef(activeSession);
  activeSessionRef.current = activeSession;
  const selectedProjectRef = useRef(selectedProject);
  selectedProjectRef.current = selectedProject;
  const fetchProjectDataRef = useRef(fetchProjectData);
  fetchProjectDataRef.current = fetchProjectData;
  const fetchSessionsRef = useRef(fetchSessions);
  fetchSessionsRef.current = fetchSessions;
  const refetchProm = useCallback(() => {
    void fetchSessionsRef.current(false);
    const proj = selectedProjectRef.current;
    if (proj) void fetchProjectDataRef.current(proj.projectId);
    const active = activeSessionRef.current;
    if (active) void getRef.current<PrometheusSession>(`/prometheus/sessions/${active.prometheusSessionId}`).then((r) => {
      if (r.success && r.data) setActiveSession(r.data);
    });
  }, []);
  useRefetchOnFocus(refetchProm);

  // Poll the session list (statuses change on the backend) while browsing the list.
  useEffect(() => {
    const id = setInterval(() => fetchSessionsRef.current(false), 8000);
    return () => clearInterval(id);
  }, []);

  // Poll active session (waiting_approval is paused — no need to poll until approved)
  useEffect(() => {
    if (!activeSession || ['completed', 'failed', 'waiting_approval'].includes(activeSession.status)) {
      if (pollRef.current) clearInterval(pollRef.current);
      return;
    }

    const poll = async () => {
      const result = await getRef.current<PrometheusSession>(`/prometheus/sessions/${activeSession.prometheusSessionId}`);
      if (result.success && result.data) {
        setActiveSession(result.data);
        if (['completed', 'failed', 'waiting_approval'].includes(result.data.status)) {
          if (pollRef.current) clearInterval(pollRef.current);
          void fetchSessionsRef.current(false);
          if (selectedProject) void fetchProjectDataRef.current(selectedProject.projectId);
        }
      }
    };

    pollRef.current = setInterval(poll, 3000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [activeSession?.prometheusSessionId, activeSession?.status]);

  const runPhase = async (daedalusSessionId: string, trackId: string, phase: 'pre-release' | 'post-release') => {
    if (!selectedProject) return;
    setLoading(true);
    const result = await post<PrometheusSession>('/prometheus/sessions', {
      daedalusSessionId,
      projectId: selectedProject.projectId,
      trackId,
      phase,
    });
    if (result.success && result.data) {
      setActiveSession(result.data);
      setCreating(false);
      navigate(`/prometheus/${result.data.prometheusSessionId}`);
      void fetchSessions(false);
      toast(`${phase === 'pre-release' ? 'Pre' : 'Post'}-release started!`, 'success');
    } else {
      toast(result.error?.message ?? 'Failed to start', 'error');
    }
    setLoading(false);
  };

  const handleLoadSession = (sessionId: string) => {
    setCreating(false);
    navigate(`/prometheus/${sessionId}`);
  };

  // Load the session named in the URL so /prometheus/:id is a shareable deep link.
  const loadedIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!urlSessionId) { loadedIdRef.current = null; return; }
    if (urlSessionId === loadedIdRef.current) return;
    loadedIdRef.current = urlSessionId;
    void getRef.current<PrometheusSession>(`/prometheus/sessions/${urlSessionId}`).then(async (r) => {
      if (!r.success || !r.data) return;
      setActiveSession(r.data);
      // Also select the session's project so the project picker reflects it and its
      // project-scoped data (available sessions, tracks) loads.
      if (r.data.projectId) {
        const p = await getRef.current<Project>(`/projects/${r.data.projectId}`);
        if (p.success && p.data) setSelectedProject(p.data);
      }
    });
  }, [urlSessionId]);

  const clearActive = useCallback(() => {
    setActiveSession(null);
    loadedIdRef.current = null;
    navigate('/prometheus');
  }, [navigate]);

  const handleApprove = async () => {
    if (!activeSession) return;
    setApproving(true);
    const result = await post(`/prometheus/sessions/${activeSession.prometheusSessionId}/approve`, {});
    if (result.success) {
      toast('Approved — resuming release tasks', 'success');
      const r = await getRef.current<PrometheusSession>(`/prometheus/sessions/${activeSession.prometheusSessionId}`);
      if (r.success && r.data) setActiveSession(r.data);
      void fetchSessions(false);
    } else {
      toast(result.error?.message ?? 'Failed to approve', 'error');
    }
    setApproving(false);
  };

  const isProcessing = activeSession?.status === 'running' || activeSession?.status === 'pending';

  // Derive selected track info for Start New Release
  const selectedTrack = tracks.find((t) => t.trackId === selectedTrackId);
  const hasPreTasks = (selectedTrack?.preReleaseTasks ?? []).length > 0;
  const hasPostTasks = (selectedTrack?.postReleaseTasks ?? []).length > 0;

  type RunInfo = { trackName: string; phase: string; status: string; cost: number };
  const getRuns = (ds: DaedalusSession): RunInfo[] =>
    (ds as any)?.prometheusRuns as RunInfo[] ?? [];

  // Athena initiatives appear as ONE entry covering all workstreams
  const sessionLabel = (ds: DaedalusSession): string => {
    const meta = ds as any;
    if (meta.initiativeLabel) return `Initiative: ${meta.initiativeLabel} (${meta.workstreamCount} workstreams)`;
    return `${ds.tickets[0]?.title ?? 'Session'} (${ds.tickets.length} tickets)`;
  };

  // Only sessions with no runs yet can start a fresh release.
  const freshSessions = availableSessions.filter((ds) => getRuns(ds).length === 0);

  // Group Prometheus sessions into In Review / Completed rows for the panel.
  const toRow = useCallback(
    (s: PrometheusSession): SessionRow => {
      const cfg = statusDisplay[s.status] ?? statusDisplay.pending;
      const shortDaedalusId = s.daedalusSessionId.slice(0, 8);
      const baseTitle =
        trackNameById[s.trackId] ??
        s.trackName ??
        `Release ${s.phase ?? ''} ${shortDaedalusId}`.trim();
      const title = s.dryRun ? `🎭 ${baseTitle} (rehearsal)` : baseTitle;
      return {
        id: s.prometheusSessionId,
        title,
        statusText: cfg.text,
        statusBadge: cfg.badge,
        statusDot: cfg.dot,
        projectName: projectNameById[s.projectId],
        createdAt: s.createdAt,
        updatedAt: s.updatedAt,
        meta: s.phase,
      };
    },
    [projectNameById, trackNameById],
  );

  const sections = useMemo<SessionSection[]>(() => {
    const byRecent = (a: PrometheusSession, b: PrometheusSession) =>
      new Date(b.updatedAt || b.createdAt).getTime() - new Date(a.updatedAt || a.createdAt).getTime();
    return SESSION_GROUPS.map((g) => ({
      key: g.key,
      title: g.title,
      rows: prometheusSessions
        .filter((s) => g.statuses.includes(s.status))
        .sort(byRecent)
        .map(toRow),
    }));
  }, [prometheusSessions, toRow]);

  return (
    <div className="max-w-5xl animate-rise">
      <PageHeader
        mark="P"
        markBg="bg-prometheus/12"
        markText="text-prometheus"
        title="Prometheus"
        subtitle="Communicate releases to your team."
      />

      {/* Project filter (optional) — scopes the session list; empty = all projects */}
      <div className="mb-5">
        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
          Filter by project
          <span className="text-xs text-gray-500 font-normal ml-2">Optional — leave empty to see every project</span>
        </label>
        <ProjectSelector value={selectedProject?.projectId} onChange={(p) => { setSelectedProject(p); clearActive(); }} />
      </div>

      {/* Session list — hidden while a session is open or the start-release form is up */}
      {!activeSession && !creating && (
        <SessionListPanel
          sections={sections}
          onSelect={handleLoadSession}
          onNewSession={() => setCreating(true)}
          newSessionLabel="Start New Release"
          showProject={!selectedProject}
          loading={listLoading}
          emptyText="No release sessions yet."
          className="mb-6"
        />
      )}

      {/* Start New Release form */}
      {!activeSession && creating && (
        <div className="space-y-5 mb-6">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold text-gray-900">Start New Release</h3>
            <Button variant="ghost" size="sm" onClick={() => setCreating(false)}>
              ← Back to releases
            </Button>
          </div>

          {!selectedProject ? (
            <div className="text-sm text-gray-600 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2.5">
              Pick a project above to start a release.
            </div>
          ) : tracks.length === 0 ? (
            <EmptyState title="No release tracks configured" hint="Go to Settings → Release Tracks to create one." />
          ) : freshSessions.length === 0 ? (
            <div className="text-center py-6 text-gray-400 bg-white rounded-2xl border border-slate-100 shadow-sm shadow-slate-200/50 text-sm">
              No new sessions available.
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_1px_2px_rgba(15,23,42,.04),0_10px_30px_-14px_rgba(15,23,42,.18)] p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Merged Session</label>
                <select className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm" value={selectedDaedalusId ?? ''} onChange={(e) => setSelectedDaedalusId(e.target.value || null)}>
                  <option value="">Select a merged session...</option>
                  {freshSessions.map((ds) => (
                    <option key={ds.daedalusSessionId} value={ds.daedalusSessionId}>
                      {sessionLabel(ds)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Release Track</label>
                <select className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm" value={selectedTrackId ?? ''} onChange={(e) => setSelectedTrackId(e.target.value || null)}>
                  <option value="">Select a track...</option>
                  {tracks.map((track) => (
                    <option key={track.trackId} value={track.trackId}>
                      {track.name} — {track.description}
                    </option>
                  ))}
                </select>
              </div>

              {selectedDaedalusId && selectedTrackId && selectedTrack && (
                <div className="flex gap-2">
                  {hasPreTasks && (
                    <Button onClick={() => runPhase(selectedDaedalusId!, selectedTrackId!, 'pre-release')} loading={loading} size="lg">
                      Run Pre-Release Tasks
                    </Button>
                  )}
                  {hasPostTasks && (
                    <Button onClick={() => runPhase(selectedDaedalusId!, selectedTrackId!, 'post-release')} loading={loading} size="lg" variant={hasPreTasks ? 'secondary' : 'primary'}>
                      Run Post-Release Tasks
                    </Button>
                  )}
                  <Button
                    variant="secondary"
                    size="lg"
                    loading={loading}
                    onClick={async () => {
                      if (!confirm('Skip release tasks and mark this session as complete?')) return;
                      setLoading(true);
                      const result = await post<PrometheusSession>('/prometheus/sessions', {
                        daedalusSessionId: selectedDaedalusId,
                        projectId: selectedProject!.projectId,
                        trackId: selectedTrackId,
                        phase: 'post-release',
                        skip: true,
                      });
                      if (result.success && result.data) {
                        setActiveSession(result.data);
                        setCreating(false);
                        navigate(`/prometheus/${result.data.prometheusSessionId}`);
                        void fetchSessions(false);
                        toast('Release marked as complete', 'success');
                      } else {
                        toast(result.error?.message ?? 'Failed', 'error');
                      }
                      setLoading(false);
                    }}
                  >
                    Complete Without Tasks
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Active session view */}
      {activeSession && (
        <div className="space-y-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <StatusBadge status={activeSession.status} />
              <span className="text-sm font-medium text-gray-700">{activeSession.trackName}</span>
              {activeSession.driveFolderUrl && (
                <a
                  href={activeSession.driveFolderUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs font-medium px-2 py-0.5 rounded-full bg-sky-50 text-sky-800 border border-sky-200 hover:bg-sky-100"
                  title="Google Drive folder holding this run's deliverables"
                >
                  📁 {activeSession.driveFolderName ?? 'Drive folder'}
                </a>
              )}
              {activeSession.dryRun && (
                <span
                  className="text-xs font-medium px-2 py-0.5 rounded-full bg-violet-100 text-violet-800 border border-violet-200"
                  title="Rehearsal: content is generated but nothing is sent to Slack/Confluence/email; the plan is not frozen and its preview stays up."
                >
                  🎭 Rehearsal — nothing is sent
                </span>
              )}
              <UsageBadge usage={activeSession.usage} />
            </div>
            <div className="flex items-center gap-2">
              {activeSession.status === 'running' && (
                <Button variant="danger" size="sm" onClick={async () => {
                  await post(`/prometheus/sessions/${activeSession.prometheusSessionId}/reset`, {});
                  const r = await getRef.current<PrometheusSession>(`/prometheus/sessions/${activeSession.prometheusSessionId}`);
                  if (r.success && r.data) setActiveSession(r.data);
                  toast('Stopped', 'info');
                }}>Stop</Button>
              )}
              <Button variant="danger" size="sm" onClick={async () => {
                if (!confirm('Delete this release session?')) return;
                await post(`/prometheus/sessions/${activeSession.prometheusSessionId}/delete`, {});
                setPrometheusSessions((prev) => prev.filter((s) => s.prometheusSessionId !== activeSession.prometheusSessionId));
                clearActive();
                if (selectedProject) {
                  const avail = await getRef.current<DaedalusSession[]>(`/prometheus/available-sessions?projectId=${selectedProject.projectId}`);
                  if (avail.success && avail.data) setAvailableSessions(avail.data);
                }
                toast('Deleted', 'info');
              }}>Delete</Button>
              <Button variant="ghost" size="sm" onClick={clearActive}>Back</Button>
            </div>
          </div>

          {/* Waiting approval banner */}
          {activeSession.status === 'waiting_approval' && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center justify-between gap-4">
              <p className="text-sm text-amber-800">
                Paused for approval — open the deliverable in Google Drive below and <strong>edit it directly</strong>.
                On approve, the current Doc is re-read from Drive and becomes the source for the remaining tasks.
              </p>
              <Button size="sm" loading={approving} onClick={handleApprove}>Approve &amp; Continue</Button>
            </div>
          )}

          {/* Processing spinner */}
          {isProcessing && (
            <div className="flex flex-col items-center py-6 bg-white rounded-2xl border border-slate-100 shadow-sm shadow-slate-200/50">
              <Spinner size="lg" className="text-primary mb-3" />
              <p className="text-sm text-gray-500">Executing release tasks...</p>
            </div>
          )}

          {/* Task progress table */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm shadow-slate-200/50 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                <tr>
                  <th className="px-4 py-3 text-left">Department</th>
                  <th className="px-4 py-3 text-left">Task</th>
                  <th className="px-4 py-3 text-left">Type</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Result</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {activeSession.tasks.map((task) => {
                  const ts = taskStatusConfig[task.status] ?? taskStatusConfig.pending;
                  const showContent = !!task.generatedContent && task.type !== 'gtm_asset';
                  return (
                    <Fragment key={task.taskId}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-gray-700 font-medium">{task.department}</td>
                        <td className="px-4 py-3 text-gray-600">{task.name}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="text-xs bg-gray-100 text-gray-500 px-2.5 py-0.5 rounded-full whitespace-nowrap">
                            {taskTypeIcons[task.type] ?? task.type}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-medium ${ts.color}`}>
                            {task.status === 'running' && <Spinner size="sm" className="inline mr-1" />}
                            {ts.text}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500">{task.deliveryResult ?? task.error ?? '-'}</td>
                      </tr>
                      {showContent && (
                        <tr className="bg-gray-50/60">
                          <td colSpan={5} className="px-4 py-2">
                            <details open={!!activeSession.dryRun}>
                              <summary className="text-xs font-medium text-gray-600 cursor-pointer select-none">
                                Generated content
                              </summary>
                              <div className="mt-2 bg-white border border-gray-200 rounded-lg p-3 text-sm max-h-96 overflow-auto">
                                <Markdown>{task.generatedContent!}</Markdown>
                              </div>
                            </details>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Deliverables */}
          {(activeSession.deliverables?.length ?? 0) > 0 && (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm shadow-slate-200/50 p-5">
              <h3 className="text-base font-semibold text-gray-900 mb-2">Deliverables</h3>
              <ul className="space-y-1.5">
                {(activeSession.deliverables ?? []).map((d, i) => (
                  <li key={i} className="flex items-baseline gap-2">
                    <a href={d.url} target="_blank" rel="noreferrer" className="text-sm text-primary hover:underline">
                      {d.name}
                    </a>
                    <span className="text-xs text-gray-500">{d.taskName}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Activity log */}
          {activeSession.messages.length > 0 && (
            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-2">Activity</h3>
              <ChatPanel
                messages={activeSession.messages}
                onSend={() => {}}
                loading={!!isProcessing}
                disabled={true}
                agentName="Prometheus"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
